import React, { useEffect } from 'react';
import { Box, Button, Divider, Typography, styled, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import AddNewCoachModal from './AddNewCoachModal';
import CustomDatePicker from '../DatePicker/DatePicker';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { COACHES_ROUTE } from '../../utils/routes';
import CustomPhoneNumberInput from '../CustomPhoneNumberInput/CustomPhoneNumberInput';
import { pronounOptions } from '../../types/client';
import { coachTypeOptions, timeZoneOptions, coachDescriptions } from '../../types/coach';
import { getInitials, sanitizeInputName } from '../../utils/userInputUtils';
import { ICreateCoachInput, addCoachRequest } from '../../redux/coach/coachSlice';
import { useAppDispatch } from '../../redux/hooks';
import {
  validationSchema,
  initialValues,
  FormValues,
} from '../../utils/validations/coachDetailsForm';
import LabeledInput from '../Common/LabeledInput';
import StyledButton from '../Common/StyledButton';

const ImageContainer = styled(Box)({
  position: 'relative',
  width: '100px',
  height: '100px',
});

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: 6,
    top: 18,
    padding: '13px 4px',
    borderRadius: '50%',
  },
}));

function AddCoach() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const organizationId = 'decb4748-9dba-4672-b0c2-88aad43fc307';

  const handleAddCoach = (values: FormValues) => {
    const coachInput: ICreateCoachInput = {
      CreateCoach: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        dob: values.dob,
        pronoun: values.pronoun,
        organizationId,
        archived: false,
        addressLine1: '',
        addressLine2: '',
        tp: values.phoneNumber,
        timeZone: values.timeZone,
        coachType: values.coachType,
        description: values.description,
      },
    };
    dispatch(addCoachRequest(coachInput));
    navigate(COACHES_ROUTE);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleAddCoach,
  });

  const handleNameChange = (field: string, value: string) => {
    if (field === 'email') {
      formik.handleChange(field)(value.toLowerCase());
    } else {
      formik.handleChange(field)(sanitizeInputName(value));
    }
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    if (pNumber) {
      formik.setFieldValue('phoneNumber', pNumber);
      formik.handleChange('phoneNumber')(pNumber);
    }
  };

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);

  const handleDateChange = (field: string, value: string) => {
    formik.setFieldValue(field, value);
    formik.handleChange(field)(value);
  };

  const initials = getInitials(formik.values.firstName, formik.values.lastName);

  useEffect(() => {
    if (formik.values.coachType) {
      formik.setFieldValue(
        'description',
        t(coachDescriptions[formik.values.coachType as keyof typeof coachDescriptions])
      );
    }
  }, [formik.values.coachType]);

  return (
    <AddNewCoachModal
      isOpen
      onClose={() => {
        navigate(COACHES_ROUTE);
      }}
    >
      <Grid item xs={12} bgcolor="background.paper" p="32px">
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid gap="36px" display="flex" flexDirection="column">
            {/* first row  */}
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h4" color="primary">
                {t('topbar.addNewCoach')}
              </Typography>
              <img
                src="/assets/icons/x-orange.svg"
                alt="close"
                onClick={() => navigate(COACHES_ROUTE)}
                role="presentation"
                style={{ cursor: 'pointer' }}
              />
            </Box>
            {/* second row */}
            <Box>
              <StyledBadge badgeContent={<EditIcon fontSize="small" />} color="primary">
                <ImageContainer mb="24px">
                  <Box
                    bgcolor="primary.light"
                    height="100%"
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="h3"
                      textAlign="center"
                      color="primary.main"
                      style={{ opacity: 0.3, letterSpacing: '-0.1em' }}
                    >
                      {initials}
                    </Typography>
                  </Box>
                </ImageContainer>
              </StyledBadge>

              <Box>
                {/* third row */}
                <Grid display="flex" gap="12px">
                  <Grid item xs={6}>
                    <LabeledInput
                      label={`${t('addNewCoach.firstName')}`}
                      id="first-name"
                      type="text"
                      name="firstName"
                      value={formik.values.firstName}
                      handleChange={(event) => handleNameChange('firstName', event.target.value)}
                      onBlur={formik.handleBlur}
                      error={formik.touched.firstName ? formik.errors.firstName : undefined}
                      placeholder={t('addNewCoach.firstName')}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <LabeledInput
                      label={`${t('addNewCoach.lastName')}`}
                      id="last-name"
                      type="text"
                      name="lastName"
                      value={formik.values.lastName}
                      handleChange={(event) => handleNameChange('lastName', event.target.value)}
                      onBlur={formik.handleBlur}
                      error={formik.touched.lastName ? formik.errors.lastName : undefined}
                      placeholder={t('addNewCoach.lastName')}
                      required
                    />
                  </Grid>
                </Grid>
                {/* fourth row */}
                <Box display="flex" gap="12px">
                  <Grid item xs={6}>
                    <CustomDatePicker
                      date={formik.values.dob || ''}
                      error={formik.touched.dob ? formik.errors.dob : undefined}
                      handleOneTimeChange={handleDateChange}
                      minDate={minDate.toISOString().split('T')[0]}
                      maxDate={new Date().toISOString().split('T')[0]}
                      label={`${t('addNewCoach.birthday')}`}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomDropdown
                      name="pronoun"
                      options={pronounOptions}
                      label={t('addNewCoach.pronoun')}
                      value={formik.values.pronoun}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.pronoun ? formik.errors.pronoun : undefined}
                      required
                      isTranslationProvided
                    />
                  </Grid>
                </Box>
                <Box />
                {/* fifth row */}
                <Box display="flex" gap="12px">
                  <Grid item xs={6}>
                    <LabeledInput
                      label={`${t('addNewCoach.email')}`}
                      id="email"
                      type="email"
                      value={formik.values.email}
                      handleChange={(event) => handleNameChange('email', event.target.value)}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email ? formik.errors.email : undefined}
                      placeholder={t('addNewCoach.email')}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomPhoneNumberInput
                      value={formik.values.phoneNumber}
                      handleChange={handlePhoneNumberChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
                      required
                    />
                  </Grid>
                </Box>
                {/* sixth row */}

                {/* <Box width="415px">
              <Typography variant="meta" color="primary">
                Organization *
              </Typography>
              <CustomDropdown
                options={pronounOptions}
                label="pronoun"
                value={pronoun}
                onChange={handleOragnizationChange}
              />
            </Box> */}
                <Grid item xs={6}>
                  <CustomDropdown
                    name="timeZone"
                    options={timeZoneOptions}
                    label={t('addNewCoach.timeZone')}
                    value={formik.values.timeZone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.timeZone ? formik.errors.timeZone : undefined}
                    required
                    isTranslationProvided
                  />
                </Grid>
              </Box>
            </Box>
            {/* seventh row */}
            <Box display="flex" flexDirection="column" gap="5px">
              <Grid item xs={6}>
                <CustomDropdown
                  name="coachType"
                  options={coachTypeOptions}
                  label={t('addNewCoach.coachType')}
                  value={formik.values.coachType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.coachType ? formik.errors.coachType : undefined}
                  required
                  isTranslationProvided
                />
              </Grid>
              <Grid item xs={12}>
                <LabeledInput
                  label={`${t('addNewCoach.description')}`}
                  id="description"
                  type="text"
                  value={formik.values.description}
                  handleChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description ? formik.errors.description : undefined}
                  placeholder={t('addNewCoach.description')}
                  required
                  multiline
                />
              </Grid>
            </Box>
            {/* Eight row */}
            <Divider />
            <Box display="flex" flexDirection="row-reverse">
              <StyledButton>{t('addNewCoach.addCoach')}</StyledButton>
            </Box>
          </Grid>
        </form>
      </Grid>
    </AddNewCoachModal>
  );
}

export default AddCoach;
